// frontend/packages/business-console/src/pages/Invoices/InvoiceActivityTimeline.jsx
//
// Right-rail activity timeline for the Invoice detail page. Mirror of
// QuoteActivityTimeline. v1 derives events from invoice timestamps +
// payment timestamps (since payments are the most interesting events
// on an invoice). Swaps to auditLogs as the source of truth when that
// read endpoint ships.

import React from 'react';
import { Box, Divider, Paper, Stack, Typography } from '@mui/material';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import TimerOffOutlinedIcon from '@mui/icons-material/TimerOffOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';

function fmtUsd(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format((cents || 0) / 100);
}

function formatDateTime(input) {
  if (!input) return null;
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function buildEvents(invoice, payments) {
  if (!invoice) return [];
  const events = [];

  if (invoice.createdAt) {
    events.push({
      at: invoice.createdAt,
      icon: EditNoteOutlinedIcon,
      tone: 'default',
      title: 'Drafted',
    });
  }
  if (invoice.sentAt) {
    events.push({
      at: invoice.sentAt,
      icon: SendOutlinedIcon,
      tone: 'info',
      title: 'Sent to customer',
    });
  }
  if (invoice.viewedAt) {
    events.push({
      at: invoice.viewedAt,
      icon: VisibilityOutlinedIcon,
      tone: 'warning',
      title: 'Viewed by customer',
    });
  }

  // Each succeeded payment becomes a timeline event.
  for (const p of payments || []) {
    if (p.state === 'succeeded' && p.paidAt) {
      events.push({
        at: p.paidAt,
        icon: PaymentsOutlinedIcon,
        tone: 'success',
        title: `Payment received  ${fmtUsd(p.amount)}`,
        detail: methodDetail(p),
      });
    }
    // Refunds within a payment row also surface
    for (const r of p.refunds || []) {
      events.push({
        at: r.refundedAt,
        icon: ReplayOutlinedIcon,
        tone: 'warning',
        title: `Refunded  −${fmtUsd(r.amount)}`,
        detail: r.reason || null,
      });
    }
  }

  if (invoice.paidAt) {
    events.push({
      at: invoice.paidAt,
      icon: PaymentsOutlinedIcon,
      tone: 'success',
      title: 'Marked paid in full',
    });
  }
  if (invoice.voidedAt) {
    events.push({
      at: invoice.voidedAt,
      icon: CancelOutlinedIcon,
      tone: 'error',
      title: 'Voided',
    });
  }
  if (invoice.state === 'overdue') {
    events.push({
      at: invoice.updatedAt,
      icon: TimerOffOutlinedIcon,
      tone: 'error',
      title: 'Marked overdue',
    });
  }
  // Dunning reminders (Item 3). We only have the count + most-recent
  // timestamp (not each individual send), so this is one summarizing event.
  if (invoice.reminderCount > 0 && invoice.lastReminderSentAt) {
    const n = invoice.reminderCount;
    events.push({
      at: invoice.lastReminderSentAt,
      icon: NotificationsActiveOutlinedIcon,
      tone: 'warning',
      title: `${n} payment reminder${n > 1 ? 's' : ''} sent`,
      detail: n > 1 ? 'most recent shown' : null,
    });
  }

  // Newest first
  return events
    .filter((e) => e.at)
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

function methodDetail(p) {
  if (!p) return null;
  const m =
    p.paymentMethod === 'card'
      ? 'Card'
      : p.paymentMethod === 'ach'
        ? 'ACH'
        : p.paymentMethod || null;
  return m;
}

const TONE_COLOR = {
  default: 'text.secondary',
  info: 'info.main',
  warning: 'warning.main',
  success: 'success.main',
  error: 'error.main',
};

export default function InvoiceActivityTimeline({ invoice, payments }) {
  const events = buildEvents(invoice, payments);

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: 'text.secondary',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          display: 'block',
          mb: 1,
        }}
      >
        Activity
      </Typography>
      {events.length === 0 ? (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Nothing yet. Activity will appear here as the invoice moves.
        </Typography>
      ) : (
        <Stack divider={<Divider />} spacing={0}>
          {events.map((e, idx) => {
            const Icon = e.icon;
            return (
              <Box key={idx} sx={{ py: 1.25, display: 'flex', gap: 1.5 }}>
                <Icon
                  fontSize="small"
                  sx={{
                    color: TONE_COLOR[e.tone] || 'text.secondary',
                    flexShrink: 0,
                    mt: 0.25,
                  }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: 'text.primary',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {e.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary' }}
                  >
                    {formatDateTime(e.at) || '—'}
                    {e.detail ? `  ·  ${e.detail}` : ''}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
}
